import type { ComponentProps, MouseEventHandler, ReactNode } from "react";
import { SidebarProjectActionsButton } from "./SidebarProjectActionsButton";
import { SidebarProjectNewThreadButton } from "./SidebarProjectNewThreadButton";
import { SidebarProjectRemovalIndicator } from "./SidebarProjectRemovalIndicator";

type RemovalIndicatorProps = ComponentProps<typeof SidebarProjectRemovalIndicator>;

interface SidebarProjectRowActionsProps {
  /** The group's own environment badge, drawn ahead of the row's own actions. */
  readonly environmentIndicator: ReactNode;
  readonly isRemoving: boolean;
  readonly removalStepLabel: RemovalIndicatorProps["stepLabel"];
  /** `Exclude` drops the prop's own optionality: absent and null are one state here. */
  readonly removalFailure: Exclude<RemovalIndicatorProps["failure"], undefined>;
  readonly isRemovalActivelyRunning: boolean;
  readonly onRetryRemoval: MouseEventHandler<HTMLButtonElement>;
  readonly onProjectActionsClick: MouseEventHandler<HTMLButtonElement>;
  readonly createThreadAriaDisabled: boolean;
  readonly canCreateThread: boolean;
  readonly createThreadLabel: string;
  readonly createThreadTooltip: string;
  readonly onCreateThreadClick: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Everything a sidebar project row draws to the right of its name.
 *
 * While a removal is in flight the row shows ONLY its removal state.
 *
 * Two reasons, and the second is a correctness one: this slot renders OUTSIDE
 * the element `isBusy` disables, so the new-thread "+" stayed live on a project
 * that was being deleted. It also has to give up its width — the row reserves a
 * fixed `pr-14` for this slot, and a spinner competing with the button would
 * push the indicator over the project name.
 *
 * The indicator is driven by the ROW's removal state, not the dialog's, so it
 * survives dismissal/reload: a multi-minute durable operation must never be
 * running with nothing on screen.
 */
export function SidebarProjectRowActions({
  environmentIndicator,
  isRemoving,
  removalStepLabel,
  removalFailure,
  isRemovalActivelyRunning,
  onRetryRemoval,
  onProjectActionsClick,
  createThreadAriaDisabled,
  canCreateThread,
  createThreadLabel,
  createThreadTooltip,
  onCreateThreadClick,
}: SidebarProjectRowActionsProps) {
  return (
    <>
      {environmentIndicator ?? null}
      {isRemoving ? (
        <SidebarProjectRemovalIndicator
          isRemoving
          stepLabel={removalStepLabel}
          failure={removalFailure}
          retryDisabled={isRemovalActivelyRunning}
          {...(removalFailure ? { onRetry: onRetryRemoval } : {})}
        />
      ) : (
        <>
          <SidebarProjectActionsButton onClick={onProjectActionsClick} />
          <SidebarProjectNewThreadButton
            ariaDisabled={createThreadAriaDisabled}
            canCreateThread={canCreateThread}
            label={createThreadLabel}
            onClick={onCreateThreadClick}
            tooltip={createThreadTooltip}
          />
        </>
      )}
    </>
  );
}
